/**
 * The owner's weight-display preference — the single place every surface asks.
 *
 * Exactly parallel to lib/date.ts's date-format preference, and for the same reason:
 * storage is canonical (kg, always) and the preference governs presentation only.
 * Conversion happens at the display boundary, and rounding happens for display and
 * never for storage.
 *
 * Entering a weight in lb already worked — 10.2 lb saved correctly as 4.63 kg — but
 * the choice lived in one component's local state, so the history list, the trend
 * sentence, the chart axis, the input placeholder and the vet prep sheet all kept
 * reading kg. Someone working in pounds never saw a pound again after pressing Save.
 */

import { useAuthStore } from '../../stores/authStore';
import type { WeightDisplayUnit } from './types';

export const DEFAULT_WEIGHT_UNIT: WeightDisplayUnit = 'kg';

export function isWeightUnit(value: unknown): value is WeightDisplayUnit {
  return value === 'kg' || value === 'lb';
}

/** The current user's stored preference, or the default when unauthenticated.
 *  Module-level read — use the hook in components that must re-render on change. */
export function getCurrentWeightUnit(): WeightDisplayUnit {
  const pref = useAuthStore.getState().user?.weight_unit;
  return isWeightUnit(pref) ? pref : DEFAULT_WEIGHT_UNIT;
}

/** React hook returning the current preference, re-rendering when it changes. */
export function useWeightUnitPref(): WeightDisplayUnit {
  return useAuthStore((state) => {
    const pref = state.user?.weight_unit;
    return isWeightUnit(pref) ? pref : DEFAULT_WEIGHT_UNIT;
  });
}
